import {
  Button,
  ConfirmModalProvider,
  IconButton,
  Loading,
  Menu,
  MenuItem,
  MenuTrigger,
  notify,
  Tooltip,
  useConfirmModal,
} from '@affine/component';
import { AnimatedPillSegment } from '@affine/core/components/animation/animated-pill-segment';
import {
  AuthService,
  defaultWorkspaceApiClient,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import { useObjectPermissionOnlineOnlyState } from '@affine/core/modules/permissions/utils/object-permission-ui';
import { PermissionExpandableSection } from '@affine/core/modules/permissions/views/permission-section';
import { WorkspaceQuotaService } from '@affine/core/modules/quota';
import { copyTextToClipboard } from '@affine/core/utils/clipboard';
import { useI18n } from '@affine/i18n';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import { CopyIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as styles from './external-access-section.css';

type ExternalAccessRole = 'Reader' | 'Editor';
type DurationHours = 24 | 168 | 720;
type DurationSelection = DurationHours | 'keep';

interface ExternalAccessState {
  link: {
    id: string;
    revision: number;
    role: ExternalAccessRole;
    expiresAt: string;
    status: 'Active' | 'Disabled';
    updatedAt: string;
  };
  activePrincipalCount: number;
  quota: { plan: string; limit: number };
  sharePath: string;
}

interface ExternalAccessPrincipal {
  id: string;
  displayName: string;
  role: ExternalAccessRole;
  expiresAt: string;
}

interface ExternalAccessPrincipalPage {
  items: ExternalAccessPrincipal[];
  nextCursor: string | null;
}

const confirmedState = new Map<string, ExternalAccessState | null>();

function endpoint(workspaceId: string, docId: string) {
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/docs/${encodeURIComponent(docId)}/external-access`;
}

function principalsEndpoint(
  workspaceId: string,
  docId: string,
  query: string,
  cursor?: string
) {
  const params = new URLSearchParams({ take: '24' });
  if (query.trim()) params.set('query', query.trim());
  if (cursor) params.set('cursor', cursor);
  return `${endpoint(workspaceId, docId)}/principals?${params}`;
}

function absoluteShareUrl(path: string) {
  return new URL(path, window.location.origin).toString();
}

interface ExternalAccessSectionProps {
  workspaceId: string;
  docId: string;
  disabled?: boolean;
}

/**
 * "Guests" section of the doc share dialog: the only entry point for guests.
 * External guests open the link, enter a name and are granted exactly this
 * document. House guests (named doc/folder invitations) cannot be created.
 */
export function ExternalAccessSection(props: ExternalAccessSectionProps) {
  // Keep confirmation portals in the sharing dialog's React/Radix layer.
  return (
    <ConfirmModalProvider>
      <ExternalAccessSectionContent {...props} />
    </ConfirmModalProvider>
  );
}

function ExternalAccessSectionContent({
  workspaceId,
  docId,
  disabled = false,
}: ExternalAccessSectionProps) {
  const t = useI18n();
  const online = useObjectPermissionOnlineOnlyState();
  const account = useLiveData(useService(AuthService).session.account$);
  const server = useService(WorkspaceServerService).server;
  const quotaService = useService(WorkspaceQuotaService);
  const planLimit = useLiveData(quotaService.quota.externalAccessPerDocLimit$);
  const { openConfirmModal } = useConfirmModal();
  const cacheKey = `${server?.baseUrl ?? 'offline'}:${account?.id ?? 'anonymous'}:${workspaceId}:${docId}`;
  const hasCached = confirmedState.has(cacheKey);
  const [snapshot, setSnapshot] = useState<{
    key: string;
    data: ExternalAccessState | null;
  } | null>(() =>
    hasCached
      ? { key: cacheKey, data: confirmedState.get(cacheKey) ?? null }
      : null
  );
  const state =
    snapshot?.key === cacheKey ? snapshot.data : confirmedState.get(cacheKey);
  const [loading, setLoading] = useState(!hasCached);
  const [stale, setStale] = useState(hasCached);
  const [loadError, setLoadError] = useState(false);
  const [mutationError, setMutationError] = useState(false);
  const [pending, setPending] = useState(false);
  const [role, setRole] = useState<ExternalAccessRole>('Editor');
  const [duration, setDuration] = useState<DurationSelection>(168);
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [people, setPeople] = useState<ExternalAccessPrincipal[]>([]);
  const [peopleCursor, setPeopleCursor] = useState<string | null>(null);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<AbortController | null>(null);
  const peopleRequestRef = useRef<AbortController | null>(null);
  const peopleListRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;
    const cached = confirmedState.has(cacheKey);
    setLoadError(false);
    setStale(cached);
    setLoading(true);
    try {
      const next =
        await defaultWorkspaceApiClient.requestJson<ExternalAccessState | null>(
          {
            workspaceId,
            path: endpoint(workspaceId, docId),
            init: { signal: request.signal },
            category: 'permissions',
            mode: 'foreground',
            priority: 'foreground',
            key: `external-access:${docId}`,
          }
        );
      if (request.signal.aborted) return;
      confirmedState.set(cacheKey, next);
      while (confirmedState.size > 64) {
        confirmedState.delete(confirmedState.keys().next().value!);
      }
      setSnapshot({ key: cacheKey, data: next });
      setRole(next?.link.role ?? 'Editor');
      setDuration(
        next?.link.status === 'Active' &&
          new Date(next.link.expiresAt).getTime() > Date.now()
          ? 'keep'
          : 168
      );
      setStale(false);
    } catch {
      if (!request.signal.aborted) {
        setLoadError(true);
        setStale(cached);
      }
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }, [cacheKey, docId, workspaceId]);

  useEffect(() => {
    const cached = confirmedState.get(cacheKey);
    const cacheHit = confirmedState.has(cacheKey);
    setSnapshot(cacheHit ? { key: cacheKey, data: cached ?? null } : null);
    setRole(cached?.link.role ?? 'Editor');
    setDuration(
      cached?.link.status === 'Active' &&
        new Date(cached.link.expiresAt).getTime() > Date.now()
        ? 'keep'
        : 168
    );
    setStale(cacheHit);
    setLoadError(false);
    setMutationError(false);
  }, [cacheKey]);

  useEffect(() => {
    quotaService.quota.revalidate();
  }, [quotaService]);

  useEffect(() => {
    if (!online.offline) void load();
    else {
      setLoading(false);
      setLoadError(true);
    }
    return () => {
      requestRef.current?.abort();
      peopleRequestRef.current?.abort();
    };
  }, [load, online.offline]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || state === undefined) return;
    streamLainAnimationController.play(
      panel,
      [
        { opacity: 0.72, transform: 'translateY(2px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      'feedback'
    );
  }, [state]);

  const expired = Boolean(
    state?.link.status === 'Active' &&
    new Date(state.link.expiresAt).getTime() <= Date.now()
  );
  const active = Boolean(state?.link.status === 'Active' && !expired);
  const limit =
    state?.quota.limit === -1 ? '∞' : String(state?.quota.limit ?? 0);
  const subtitle =
    loading && state === undefined
      ? t['com.streamlain.share-menu.public-sharing.loading']()
      : loadError && state === undefined
        ? t['com.streamlain.share-menu.public-sharing.unknown']()
        : active && state
          ? t['com.streamlain.share-menu.external-access.active']({
              count: String(state.activePrincipalCount),
              limit,
            })
          : expired
            ? t['com.streamlain.share-menu.external-access.expired']()
            : t['com.streamlain.share-menu.external-access.off']();
  const authorityUnconfirmed = loadError || stale || online.offline;
  const locked =
    disabled || online.disabled || pending || loading || authorityUnconfirmed;
  const planDisabled = planLimit === 0;
  const planPending = planLimit === null;
  const settingsLocked = locked || planDisabled || planPending;
  const formattedExpiry = useMemo(
    () =>
      state
        ? new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(new Date(state.link.expiresAt))
        : '',
    [state]
  );
  const durationOptions = [
    ...(active
      ? [
          {
            value: 'keep' as const,
            label: t['com.streamlain.share-menu.external-access.keep-expiry'](),
          },
        ]
      : []),
    {
      value: 24 as const,
      label: t['com.streamlain.share-menu.external-access.one-day'](),
    },
    {
      value: 168 as const,
      label: t['com.streamlain.share-menu.external-access.seven-days'](),
    },
    {
      value: 720 as const,
      label: t['com.streamlain.share-menu.external-access.thirty-days'](),
    },
  ];
  const selectedDuration =
    durationOptions.find(option => option.value === duration) ??
    durationOptions[0];

  const loadPeople = useCallback(
    async (cursor?: string) => {
      peopleRequestRef.current?.abort();
      const request = new AbortController();
      peopleRequestRef.current = request;
      setPeopleLoading(true);
      setPeopleError(false);
      try {
        const page =
          await defaultWorkspaceApiClient.requestJson<ExternalAccessPrincipalPage>(
            {
              workspaceId,
              path: principalsEndpoint(workspaceId, docId, peopleQuery, cursor),
              init: { signal: request.signal },
              category: 'permissions',
              mode: 'foreground',
              priority: 'visible',
              key: `external-access-people:${docId}:${peopleQuery}:${cursor ?? 'first'}`,
            }
          );
        if (request.signal.aborted) return;
        setPeople(current =>
          cursor ? [...current, ...page.items] : page.items
        );
        setPeopleCursor(page.nextCursor);
      } catch {
        if (!request.signal.aborted) setPeopleError(true);
      } finally {
        if (peopleRequestRef.current === request) setPeopleLoading(false);
      }
    },
    [docId, peopleQuery, workspaceId]
  );

  useEffect(() => {
    if (!reviewEnabled || !active || online.offline) return;
    setPeople([]);
    setPeopleCursor(null);
    const timeout = window.setTimeout(() => void loadPeople(), 220);
    return () => window.clearTimeout(timeout);
  }, [active, loadPeople, online.offline, reviewEnabled]);

  useEffect(() => {
    const list = peopleListRef.current;
    if (!list || peopleLoading) return;
    const animation = streamLainAnimationController.play(
      list,
      [
        { opacity: 0.76, transform: 'translateY(2px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      'feedback'
    );
    return () => animation.cancel();
  }, [people, peopleLoading]);

  const update = async (rotate: boolean, copyAfter: boolean) => {
    if (settingsLocked) return;
    setPending(true);
    setMutationError(false);
    try {
      const result = await defaultWorkspaceApiClient.requestJson<{
        sharePath: string;
      }>({
        workspaceId,
        path: endpoint(workspaceId, docId),
        init: {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role,
            ...(duration === 'keep' && !rotate
              ? {}
              : { ttlHours: duration === 'keep' ? 168 : duration }),
            rotate,
          }),
        },
        category: 'permissions',
        mode: 'foreground',
        priority: 'foreground',
      });
      await load();
      if (copyAfter) {
        const copied = await copyTextToClipboard(
          absoluteShareUrl(result.sharePath)
        );
        if (!copied) {
          notify.error({
            title: t['com.streamlain.share-menu.external-access.copy-error'](),
          });
        } else {
          notify.success({
            title: t['com.streamlain.share-menu.external-access.copied'](),
          });
        }
      }
    } catch {
      setMutationError(true);
      setStale(true);
    } finally {
      setPending(false);
    }
  };

  const copy = async () => {
    if (!state || !active || settingsLocked) return;
    const copied = await copyTextToClipboard(absoluteShareUrl(state.sharePath));
    notify[copied ? 'success' : 'error']({
      title: copied
        ? t['com.streamlain.share-menu.external-access.copied']()
        : t['com.streamlain.share-menu.external-access.copy-error'](),
    });
  };

  const disable = async () => {
    if (settingsLocked) return;
    setPending(true);
    setMutationError(false);
    try {
      await defaultWorkspaceApiClient.request({
        workspaceId,
        path: endpoint(workspaceId, docId),
        init: { method: 'DELETE' },
        category: 'permissions',
        mode: 'foreground',
        priority: 'foreground',
      });
      await load();
    } catch {
      setMutationError(true);
      setStale(true);
    } finally {
      setPending(false);
    }
  };

  return (
    <PermissionExpandableSection
      title={t['com.streamlain.share-menu.external-access.title']()}
      subtitle={subtitle}
      defaultCollapsed
      dataTestId="external-access-section"
      onExpandedChange={setReviewEnabled}
    >
      <div ref={panelRef} className={styles.panel} aria-busy={pending}>
        <p className={styles.description}>
          {t['com.streamlain.share-menu.external-access.description']()}
        </p>
        {state ? (
          <div className={styles.statusRow}>
            <div className={styles.statusCopy}>
              <span className={styles.statusTitle}>{subtitle}</span>
              {active ? (
                <span className={styles.statusDetail}>
                  {t['com.streamlain.share-menu.external-access.active-until']({
                    date: formattedExpiry,
                  })}
                </span>
              ) : null}
            </div>
            <span
              className={styles.indicator}
              data-active={active ? 'true' : undefined}
              aria-hidden="true"
            />
          </div>
        ) : null}
        {loadError || stale || online.offline ? (
          <div className={styles.notice} role="alert">
            <span>
              {t['com.streamlain.share-menu.external-access.load-error']()}
            </span>
            <Button
              variant="plain"
              disabled={loading || online.offline}
              onClick={() => void load()}
            >
              {loading ? (
                <Loading size={14} />
              ) : (
                t['com.streamlain.share-menu.external-access.retry']()
              )}
            </Button>
          </div>
        ) : null}
        {planDisabled ? (
          <div className={styles.notice} role="status">
            {t['com.streamlain.share-menu.external-access.plan-disabled']()}
          </div>
        ) : null}
        {state === undefined && loading ? (
          <div className={styles.notice} role="status">
            <Loading size={14} />
            {t['com.streamlain.share-menu.public-sharing.loading']()}
          </div>
        ) : null}
        {state !== undefined ? (
          <>
            <div className={styles.field}>
              <span className={styles.label}>
                {t['com.streamlain.share-menu.external-access.role']()}
              </span>
              <AnimatedPillSegment
                active={role}
                ariaLabel={t[
                  'com.streamlain.share-menu.external-access.role'
                ]()}
                disabled={settingsLocked}
                options={(
                  [
                    [
                      'Reader',
                      'com.streamlain.share-menu.external-access.reader',
                    ],
                    [
                      'Editor',
                      'com.streamlain.share-menu.external-access.editor',
                    ],
                  ] as const
                ).map(([key, label]) => ({
                  key,
                  label: t[label](),
                }))}
                onChange={setRole}
              />
            </div>
            <div className={styles.field}>
              <span
                className={styles.label}
                id="external-access-duration-label"
              >
                {t['com.streamlain.share-menu.external-access.duration']()}
              </span>
              <Menu
                contentOptions={{
                  align: 'start',
                  style: {
                    width: 'var(--radix-dropdown-menu-trigger-width)',
                  },
                }}
                items={durationOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    selected={option.value === duration}
                    onSelect={() => setDuration(option.value)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              >
                <MenuTrigger
                  block
                  className={styles.durationTrigger}
                  data-testid="external-access-duration"
                  aria-labelledby="external-access-duration-label"
                  disabled={settingsLocked}
                >
                  {selectedDuration?.label ?? ''}
                </MenuTrigger>
              </Menu>
            </div>
            {mutationError ? (
              <div className={styles.notice} role="alert">
                {t['com.streamlain.share-menu.external-access.update-error']()}
              </div>
            ) : null}
            {active && state ? (
              <div className={styles.linkRow}>
                <input
                  className={styles.linkInput}
                  value={absoluteShareUrl(state.sharePath)}
                  readOnly
                  aria-label={t[
                    'com.streamlain.share-menu.external-access.copy'
                  ]()}
                  data-testid="external-access-link"
                  onFocus={event => event.currentTarget.select()}
                />
                <Tooltip
                  content={t[
                    'com.streamlain.share-menu.external-access.copy'
                  ]()}
                >
                  <IconButton
                    icon={<CopyIcon />}
                    aria-label={t[
                      'com.streamlain.share-menu.external-access.copy'
                    ]()}
                    disabled={settingsLocked}
                    data-testid="external-access-copy"
                    onClick={() => void copy()}
                  />
                </Tooltip>
              </div>
            ) : null}
            <div className={styles.actions}>
              <Button
                variant="primary"
                className={styles.primaryAction}
                data-testid="external-access-save"
                disabled={settingsLocked}
                onClick={() => void update(expired, !active)}
              >
                {pending ? (
                  <Loading size={14} />
                ) : active ? (
                  t['com.streamlain.share-menu.external-access.save']()
                ) : (
                  t['com.streamlain.share-menu.external-access.activate-copy']()
                )}
              </Button>
            </div>
            {active && state ? (
              <div className={styles.quietActions}>
                <Button
                  variant="secondary"
                  className={styles.destructiveAction}
                  disabled={settingsLocked}
                  data-testid="external-access-disable"
                  onClick={() =>
                    openConfirmModal({
                      title:
                        t[
                          'com.streamlain.share-menu.external-access.disable-title'
                        ](),
                      description: t[
                        'com.streamlain.share-menu.external-access.disable-description'
                      ]({ count: String(state.activePrincipalCount) }),
                      confirmText:
                        t[
                          'com.streamlain.share-menu.external-access.disable'
                        ](),
                      cancelText: t['Cancel'](),
                      confirmButtonOptions: { variant: 'error' },
                      onConfirm: disable,
                    })
                  }
                >
                  {t['com.streamlain.share-menu.external-access.disable']()}
                </Button>
                <Button
                  variant="secondary"
                  className={styles.quietAction}
                  disabled={settingsLocked}
                  data-testid="external-access-replace"
                  onClick={() =>
                    openConfirmModal({
                      title:
                        t[
                          'com.streamlain.share-menu.external-access.replace-title'
                        ](),
                      description: t[
                        'com.streamlain.share-menu.external-access.replace-description'
                      ]({ count: String(state.activePrincipalCount) }),
                      confirmText:
                        t[
                          'com.streamlain.share-menu.external-access.replace'
                        ](),
                      cancelText: t['Cancel'](),
                      onConfirm: () => update(true, true),
                    })
                  }
                >
                  {t['com.streamlain.share-menu.external-access.replace']()}
                </Button>
              </div>
            ) : null}
            {active && reviewEnabled ? (
              <div className={styles.people}>
                <span className={styles.label}>
                  {t['com.streamlain.share-menu.external-access.people']()}
                </span>
                <input
                  className={styles.search}
                  type="search"
                  data-testid="external-access-people-search"
                  aria-label={t[
                    'com.streamlain.share-menu.external-access.search'
                  ]()}
                  value={peopleQuery}
                  placeholder={t[
                    'com.streamlain.share-menu.external-access.search'
                  ]()}
                  onChange={event => setPeopleQuery(event.target.value)}
                />
                {peopleError ? (
                  <div className={styles.notice} role="alert">
                    <span>
                      {t[
                        'com.streamlain.share-menu.external-access.load-error'
                      ]()}
                    </span>
                    <Button
                      variant="plain"
                      disabled={peopleLoading}
                      onClick={() => void loadPeople()}
                    >
                      {t['com.streamlain.share-menu.external-access.retry']()}
                    </Button>
                  </div>
                ) : null}
                <div
                  ref={peopleListRef}
                  className={styles.peopleList}
                  aria-live="polite"
                  aria-busy={peopleLoading}
                >
                  {people.map(person => (
                    <div
                      className={styles.person}
                      key={person.id}
                      data-testid={`external-access-person-${person.id}`}
                    >
                      <span className={styles.avatar} aria-hidden="true">
                        {person.displayName.slice(0, 1).toLocaleUpperCase()}
                      </span>
                      <span className={styles.personCopy}>
                        <span className={styles.personName}>
                          {person.displayName}
                        </span>
                        <span className={styles.personRole}>
                          {t[
                            person.role === 'Reader'
                              ? 'com.streamlain.share-menu.external-access.reader'
                              : 'com.streamlain.share-menu.external-access.editor'
                          ]()}
                        </span>
                      </span>
                      <Button
                        variant="plain"
                        disabled={pending}
                        aria-label={`${t['com.streamlain.share-menu.external-access.remove']()} ${person.displayName}`}
                        onClick={() =>
                          openConfirmModal({
                            title: t[
                              'com.streamlain.share-menu.external-access.remove-title'
                            ]({ name: person.displayName }),
                            description:
                              t[
                                'com.streamlain.share-menu.external-access.remove-description'
                              ](),
                            confirmText:
                              t[
                                'com.streamlain.share-menu.external-access.remove'
                              ](),
                            cancelText: t['Cancel'](),
                            confirmButtonOptions: { variant: 'error' },
                            onConfirm: async () => {
                              setPending(true);
                              try {
                                await defaultWorkspaceApiClient.request({
                                  workspaceId,
                                  path: `${endpoint(workspaceId, docId)}/principals/${encodeURIComponent(person.id)}`,
                                  init: { method: 'DELETE' },
                                  category: 'permissions',
                                  mode: 'foreground',
                                  priority: 'foreground',
                                });
                                await Promise.all([load(), loadPeople()]);
                              } catch {
                                setMutationError(true);
                              } finally {
                                setPending(false);
                              }
                            },
                          })
                        }
                      >
                        {t[
                          'com.streamlain.share-menu.external-access.remove'
                        ]()}
                      </Button>
                    </div>
                  ))}
                  {!peopleLoading && people.length === 0 ? (
                    <div className={styles.notice}>
                      {t['com.streamlain.share-menu.external-access.empty']()}
                    </div>
                  ) : null}
                </div>
                {peopleCursor ? (
                  <Button
                    variant="plain"
                    disabled={peopleLoading}
                    onClick={() => void loadPeople(peopleCursor)}
                  >
                    {peopleLoading ? (
                      <Loading size={14} />
                    ) : (
                      t['com.streamlain.share-menu.external-access.more']()
                    )}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </PermissionExpandableSection>
  );
}
